import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import UserSelect from "features/user-select/user-select";
import { fetch as fetchCompanyUsers } from "services/user-select.service";
import {
  WorkSecurityArea,
  createWorkSecurityArea,
  setWorkSecurityAreaResponsibles,
  updateWorkSecurityArea,
} from "services/work-security-areas.service";
import { EditPanel, Toggle, useSaveWithToast } from "../../ui";

const LABEL_CLASS = "mb-1 block text-sm font-medium text-gray-700";
const INPUT_CLASS =
  "block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 md:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500";
const DANGER_BUTTON =
  "inline-flex min-h-11 items-center md:min-h-9 [@media(pointer:coarse)]:min-h-11 rounded-lg border border-red-600 bg-red-600 px-4 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60";
const TEXT_BUTTON =
  "text-sm font-medium max-md:min-h-11 disabled:cursor-not-allowed disabled:opacity-60";

/** The server's own explanation of a failure, when it sent one. */
const serverMessage = (err: any): string | undefined =>
  err?.response?.data?.message;

interface ResponsiblesPickerProps {
  companyId: string;
  selectedIds: string[];
  /** Names of the people already responsible, so the list can show them at once. */
  knownNames: Record<string, string>;
  onChange: (ids: string[]) => void;
}

function ResponsiblesPicker({
  companyId,
  selectedIds,
  knownNames,
  onChange,
}: ResponsiblesPickerProps) {
  const { t } = useTranslation();
  // The picker hands back only an id, so every company user is loaded to name a newly added person.
  const [names, setNames] = useState(knownNames);

  useEffect(() => {
    fetchCompanyUsers(companyId, {}, false, undefined, false)
      .then((data: any) => {
        setNames((current) => {
          const next = { ...current };
          for (const user of data?.usersCollection || []) {
            if (!next[user.id]) next[user.id] = user.name || user.email;
          }
          return next;
        });
      })
      .catch((err: any) => console.error("Failed to load company users", err));
  }, [companyId]);

  return (
    <div>
      <UserSelect
        onChange={(userId: string) => {
          if (userId && !selectedIds.includes(userId)) {
            onChange([...selectedIds, userId]);
          }
        }}
        value=""
        label="work_security_area_responsibles_select"
      />
      <ul className="mt-2 space-y-2">
        {selectedIds.map((userId) => (
          <li
            key={userId}
            className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2"
          >
            <span className="text-sm text-gray-900">
              {names[userId] || userId}
            </span>
            <button
              type="button"
              className={`${TEXT_BUTTON} text-red-700 hover:text-red-900`}
              onClick={() =>
                onChange(selectedIds.filter((id) => id !== userId))
              }
            >
              {t("remove")}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

interface ArchiveSectionProps {
  area: WorkSecurityArea;
  saving: boolean;
  /** Resolves to whether the change was saved. */
  onSetArchived: (isArchived: boolean) => Promise<boolean>;
}

/** Archive (after an inline confirmation) or restore; the system area can do neither. */
function ArchiveSection({ area, saving, onSetArchived }: ArchiveSectionProps) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);

  if (area.isSystem) {
    return (
      <p className="text-sm text-gray-600">
        {t("work_security_area_system_archive_disabled")}
      </p>
    );
  }

  if (area.isArchived) {
    return (
      <button
        type="button"
        className={`${TEXT_BUTTON} text-indigo-600 hover:text-indigo-800`}
        disabled={saving}
        onClick={() => onSetArchived(false)}
      >
        {t("work_security_area_restore")}
      </button>
    );
  }

  if (confirming) {
    const archive = async () => {
      if (!(await onSetArchived(true))) setConfirming(false);
    };
    return (
      <div className="space-y-2">
        <p className="text-sm text-gray-700">
          {t("work_security_area_archive_confirm_desc")}
        </p>
        <button
          type="button"
          className={DANGER_BUTTON}
          disabled={saving}
          onClick={archive}
        >
          {t("work_security_area_archive_confirm")}
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      className={`${TEXT_BUTTON} text-red-700 hover:text-red-900`}
      disabled={saving}
      onClick={() => setConfirming(true)}
    >
      {t("work_security_area_archive")}
    </button>
  );
}

interface AreaEditorProps {
  companyId: string;
  /** Absent: a new area is being created. */
  area?: WorkSecurityArea;
  /** Reloads the list of areas. */
  onChanged: () => Promise<unknown>;
  onClose: () => void;
}

/** Name, incidents, responsible people and archiving of one instruction area, edited in place. */
export default function AreaEditor({
  companyId,
  area,
  onChanged,
  onClose,
}: AreaEditorProps) {
  const { t } = useTranslation();
  const nameId = useId();
  const { save, saving, error } = useSaveWithToast();

  const [name, setName] = useState(area?.name ?? "");
  const [hasIncidents, setHasIncidents] = useState(area?.hasIncidents ?? false);
  const [responsibleIds, setResponsibleIds] = useState(
    (area?.responsibles ?? []).map((person) => person.id)
  );
  const [nameMissing, setNameMissing] = useState(false);
  // A new area exists on the server after the first save; a retry must update it, not create a second one.
  const createdAreaId = useRef<string>();

  const submit = async () => {
    const trimmedName = name.trim();
    setNameMissing(!trimmedName);
    if (!trimmedName) return;

    let responsiblesFailed = false;
    const saved = await save(
      async () => {
        const fields = { name: trimmedName, hasIncidents };
        let areaId = area?.id ?? createdAreaId.current;
        if (areaId) {
          await updateWorkSecurityArea(companyId, areaId, fields);
        } else {
          areaId = (await createWorkSecurityArea(companyId, fields)).area.id;
          createdAreaId.current = areaId;
        }
        try {
          await setWorkSecurityAreaResponsibles(
            companyId,
            areaId,
            responsibleIds
          );
        } catch (err) {
          // The area itself is saved: the list shows it, and this panel stays open with the error.
          responsiblesFailed = true;
          await onChanged();
          throw err;
        }
        await onChanged();
      },
      {
        errorMessage: (err) =>
          serverMessage(err) ||
          t(
            responsiblesFailed
              ? "work_security_area_responsibles_save_failed"
              : "work_security_area_save_failed"
          ),
      }
    );
    if (saved) onClose();
  };

  const setArchived = async (areaId: string, isArchived: boolean) => {
    const failedKey = isArchived
      ? "work_security_area_archive_failed"
      : "work_security_area_restore_failed";
    const done = await save(
      async () => {
        await updateWorkSecurityArea(companyId, areaId, { isArchived });
        await onChanged();
      },
      { errorMessage: (err) => serverMessage(err) || t(failedKey) }
    );
    if (done) onClose();
    return done;
  };

  return (
    <EditPanel
      onSave={submit}
      onCancel={onClose}
      saving={saving}
      error={nameMissing ? t("work_security_area_name_required") : error}
    >
      <div className="space-y-4">
        <div>
          <label htmlFor={nameId} className={LABEL_CLASS}>
            {t("work_security_area_name")}
          </label>
          <input
            id={nameId}
            className={INPUT_CLASS}
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={t("work_security_area_name_placeholder")}
            autoComplete="off"
          />
        </div>

        <div className="flex items-start gap-3">
          <Toggle
            checked={hasIncidents}
            onChange={setHasIncidents}
            label={t("work_security_area_can_register_incidents")}
          />
          <div>
            <div className="text-sm font-medium text-gray-900">
              {t("work_security_area_can_register_incidents")}
            </div>
            <p className="text-sm text-gray-600">
              {t("work_security_area_can_register_incidents_hint")}
            </p>
          </div>
        </div>

        <ResponsiblesPicker
          companyId={companyId}
          selectedIds={responsibleIds}
          knownNames={Object.fromEntries(
            (area?.responsibles ?? []).map((person) => [person.id, person.name])
          )}
          onChange={setResponsibleIds}
        />

        {area && (
          <div className="border-t border-gray-200 pt-3">
            <ArchiveSection
              area={area}
              saving={saving}
              onSetArchived={(isArchived) => setArchived(area.id, isArchived)}
            />
          </div>
        )}
      </div>
    </EditPanel>
  );
}
